import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Gift, Zap, ChevronRight, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const INVITE_LS_KEY = "nah_invite_code";

/**
 * Public invite landing. URL pattern: /invite/<code>
 * Resolves the code, shows who invited them, stashes the code in
 * localStorage so AuthCallback can redeem it post-OAuth, then links out
 * to Emergent Google sign-in.
 */
export default function InviteLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });
  const [agreed, setAgreed] = useState(false);
  const [agreeError, setAgreeError] = useState(false);

  useEffect(() => {
    if (!code) return;
    const upper = code.toUpperCase();
    localStorage.setItem(INVITE_LS_KEY, upper);
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/invites/${upper}`);
        setState({ loading: false, invite: { code: upper, ...r.data } });
      } catch (_e) {
        setState({ loading: false, error: "This invite is expired or invalid." });
        localStorage.removeItem(INVITE_LS_KEY);
      }
    })();
  }, [code]);

  const handleSignIn = () => {
    if (!agreed) { setAgreeError(true); return; }
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-[#0A0A0A] text-white"
      data-testid="invite-landing"
    >
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a0e1f]/80 via-black/90 to-[#1a1408]/80" />

      <div className="relative z-10 min-h-screen flex items-center justify-center px-6">
        <div className="max-w-xl w-full surface-glass border-2 border-[#FFCC00]/60 p-8 md:p-12">
          <div className="label-kicker text-[#FFCC00] mb-3 flex items-center gap-2">
            <Gift className="w-4 h-4" /> YOU&apos;VE BEEN INVITED
          </div>
          <h1 className="font-heading text-4xl md:text-6xl leading-[0.9] tracking-tight mb-6">
            <span className="text-[#FFCC00]">NITRO</span>{" "}
            <span className="text-white">AUTO</span>{" "}
            <span className="text-[#FF3B30]">HEIST</span>
          </h1>

          {state.loading && (
            <div className="text-[10px] tracking-[0.4em] text-white/40 animate-pulse" data-testid="invite-loading">
              DECRYPTING INVITE…
            </div>
          )}

          {state.error && (
            <div data-testid="invite-error">
              <p className="text-white/70 mb-6">{state.error}</p>
              <button onClick={() => navigate("/")} className="btn-ghost">Go to landing</button>
            </div>
          )}

          {state.invite && (
            <div data-testid="invite-resolved">
              <div className="flex items-center gap-4 mb-6">
                {state.invite.referrer_picture ? (
                  <img
                    src={state.invite.referrer_picture}
                    alt={state.invite.referrer_name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-[#FFCC00]"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#FFCC00]/20 border-2 border-[#FFCC00] flex items-center justify-center font-heading text-xl text-[#FFCC00]">
                    {state.invite.referrer_name?.[0]?.toUpperCase() ?? "R"}
                  </div>
                )}
                <div>
                  <div className="text-xs tracking-[0.25em] uppercase text-white/40">From</div>
                  <div className="font-heading text-2xl" data-testid="invite-referrer-name">
                    {state.invite.referrer_name}
                  </div>
                </div>
              </div>
              <p className="text-white/70 leading-relaxed mb-6">
                Buckle up, heister. Retro streets. 20 pixel-art champs.
                The FBI on your bumper.{" "}
                <span className="text-[#FFCC00] font-mono">Both of you</span> pocket{" "}
                <span className="text-[#FFCC00] font-mono">+500 credits</span> once you sign in.
              </p>
              <button
                onClick={handleSignIn}
                className="btn-primary anim-pulse-red flex items-center gap-2"
                data-testid="invite-signin-btn"
              >
                <Zap className="w-4 h-4" /> Claim invite · Sign in
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Mini agreement gate — same content as Landing, but compact */}
              <label
                className={`mt-4 flex items-start gap-3 cursor-pointer select-none p-2.5 border transition-colors ${
                  agreeError && !agreed
                    ? "border-[#FF3B30] bg-[#FF3B30]/5"
                    : agreed
                      ? "border-[#FFCC00]/60 bg-[#FFCC00]/[0.04]"
                      : "border-white/10 hover:border-white/30"
                }`}
                data-testid="invite-agree-label"
              >
                <span className={`w-4 h-4 mt-0.5 flex-shrink-0 border-2 flex items-center justify-center ${
                  agreed ? "bg-[#FFCC00] border-[#FFCC00]" : "border-white/40"
                }`}>
                  {agreed && <Check className="w-3 h-3 text-black" strokeWidth={3.5} />}
                </span>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => { setAgreed(e.target.checked); setAgreeError(false); }}
                  className="sr-only"
                  data-testid="invite-agree-checkbox"
                />
                <span className="text-[11px] text-white/70 leading-snug">
                  13+ and I agree to the{" "}
                  <Link to="/terms" className="text-[#FFCC00] underline">Terms</Link> +{" "}
                  <Link to="/privacy" className="text-[#FFCC00] underline">Privacy</Link>.
                </span>
              </label>
              {agreeError && !agreed && (
                <div className="mt-1 text-[11px] text-[#FF3B30]" data-testid="invite-agree-error">
                  You need to agree before we sign you in.
                </div>
              )}
              <div className="mt-4 text-[10px] font-mono text-white/30 tracking-[0.3em] uppercase">
                Invite code: {state.invite.code}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
